import React from 'react';
import styled from 'styled-components';
import {
  IDGMeeting,
  formatPatientName,
  isPatientRowPrintable,
  patientRowKey,
} from './transforms';
import { useIDGSelection } from './IDGSelectionContext';
import defaults from '../../translations/en.json';

/**
 * Plain React detail cell renderer for IDG meeting master rows.
 *
 * Using a plain flex layout instead of a nested AgGridReact means wheel events
 * propagate naturally to the outer grid viewport — a nested grid installs its
 * own event listeners that swallow scroll events.
 */

const SELECTED_ROW_BG = '#005880';
const UNPRINTABLE_ROW_BG = 'grey';

const Row = styled.div<{
  $selectable?: boolean;
  $selected?: boolean;
}>`
  display: flex;
  align-items: center;
  height: 27px;
  border-bottom: 1px solid black;
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
  background-color: ${({ $selected, $selectable }) => {
    if ($selected) return SELECTED_ROW_BG;
    if ($selectable === false) return UNPRINTABLE_ROW_BG;
    return 'transparent';
  }};
  color: ${({ $selected }) => ($selected ? 'white' : 'inherit')};
`;

const Cell = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
  flex: none;
  padding: 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 27px;
`;

const FlexCell = styled.div`
  flex: 1;
  padding: 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 27px;
`;

/** Matches master grid header styling (ag-theme-hummingbird.light). */
const HeaderRow = styled(Row)`
  font-weight: 500;
  background-color: ${(props) => props.theme.palette.background.header};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  color: inherit;
  cursor: default;
`;

export const PatientDetailRenderer = (params: any) => {
  const meeting = params.data as IDGMeeting;
  const { togglePatientRow, isPatientRowSelected } = useIDGSelection();

  const handleRowClick = (
    e: React.MouseEvent,
    patient: IDGMeeting['patients'][number],
  ) => {
    e.stopPropagation();
    if (!isPatientRowPrintable(patient)) return;
    togglePatientRow(meeting.meetingKey, patient);
  };

  return (
    <>
      <HeaderRow>
        <Cell width={88}>{defaults.columnPatientNumber}</Cell>
        <Cell width={160}>{defaults.columnPatientName}</Cell>
        <FlexCell>{defaults.columnFormName}</FlexCell>
      </HeaderRow>
      {meeting.patients.map((patient) => {
        const printable = isPatientRowPrintable(patient);
        const selected = isPatientRowSelected(meeting.meetingKey, patient);
        return (
          <Row
            key={patientRowKey(meeting.meetingKey, patient)}
            $selectable={printable}
            $selected={selected}
            onClick={(e) => handleRowClick(e, patient)}
          >
            <Cell width={88}>{patient.patientNumber}</Cell>
            <Cell width={160}>{formatPatientName(patient)}</Cell>
            <FlexCell>{patient.formName}</FlexCell>
          </Row>
        );
      })}
    </>
  );
};
